import React, { useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import type { LensCard, LensCardHeight, LensCardSize, LensSection } from '~/types/leLens';

import LayoutPreview from './LayoutPreview';
import { frameForCard, swapFrames } from './canvasLayout';
import { DEFAULT_SIZE, resolveLensCardPresentation } from './cardLayout';
import { layoutWorkspaceStructure } from './layoutWorkspaceDraft';

type Props = {
  open: boolean;
  sections: Array<LensSection>;
  cards: Array<LensCard>;
  isSaving: boolean;
  onClose: () => void;
  onAutoArrange?: (cards: Array<LensCard>) => Array<LensCard>;
  onSave: (layout: { sections: Array<LensSection>; cards: Array<LensCard> }) => Promise<void>;
};

const UNGROUPED = '__ungrouped';
const DELETE_CARDS = '__delete';

type Group = { id: string; title: string; cardIds: Array<string> };
type CardFrame = { size?: LensCardSize; height?: LensCardHeight };

const FRAME_OPTIONS: Array<{ value: LensCardSize; label: string }> = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'full', label: 'Full width' },
];

const HEIGHT_OPTIONS: Array<{ value: LensCardHeight | 'auto'; label: string }> = [
  { value: 'auto', label: 'Auto' },
  { value: 'short', label: 'Short' },
  { value: 'standard', label: 'Standard' },
  { value: 'tall', label: 'Tall' },
  { value: 'xtall', label: 'Extra tall' },
];

function moved<T>(items: Array<T>, index: number, delta: number): Array<T> {
  const destination = index + delta;
  if (destination < 0 || destination >= items.length) return items;
  const next = [...items];
  [next[index], next[destination]] = [next[destination], next[index]];
  return next;
}

/**
 * The canvas is the quick, direct-manipulation path. This panel is the precise
 * counterpart: every result and section has a labelled, keyboard-operable
 * control, with a single save at the end rather than a network write per edit.
 */
export default function LayoutWorkspace({ open, sections, cards, isSaving, onClose, onSave, onAutoArrange }: Props) {
  const [initialStructure, setInitialStructure] = useState<string | null>(null);
  const [groups, setGroups] = useState<Array<Group>>([]);
  const [frames, setFrames] = useState<Record<string, CardFrame>>({});
  const [movedFrames, setMovedFrames] = useState<Record<string, LensCard['layout']>>({});
  const [resizedCardIds, setResizedCardIds] = useState<ReadonlySet<string>>(new Set());
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [arrangedTitleAlignments, setArrangedTitleAlignments] = useState<Record<string, 'left' | 'center' | 'right'>>(
    {},
  );
  const [hasArrangementPreview, setHasArrangementPreview] = useState(false);
  const [removing, setRemoving] = useState<{ id: string; destination: string } | null>(null);

  useEffect(() => {
    if (!open) {
      setInitialStructure(null);
      return;
    }
    setInitialStructure(layoutWorkspaceStructure(sections, cards));
    const known = new Set(sections.map((section) => section.id));
    setGroups([
      {
        id: UNGROUPED,
        title: '',
        cardIds: cards.filter((card) => !card.section || !known.has(card.section)).map((card) => card.id),
      },
      ...sections.map((section) => ({
        id: section.id,
        title: section.title,
        cardIds: cards.filter((card) => card.section === section.id).map((card) => card.id),
      })),
    ]);
    setFrames(Object.fromEntries(cards.map((card) => [card.id, { size: card.size, height: card.height }])));
    setMovedFrames({});
    setArrangedTitleAlignments({});
    setHasArrangementPreview(false);
    setResizedCardIds(new Set());
    setNewSectionTitle('');
    setRemoving(null);
    // The draft belongs to the open panel, not to background Lens refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const structureChanged = Boolean(
    open && initialStructure && initialStructure !== layoutWorkspaceStructure(sections, cards),
  );
  const cardsById = useMemo(() => new Map(cards.map((card) => [card.id, card])), [cards]);
  const namedGroups = groups.filter((group) => group.id !== UNGROUPED);
  const hasUnnamedSection = namedGroups.some((group) => !group.title.trim());

  const updateGroup = (id: string, update: (group: Group) => Group) =>
    setGroups((current) => current.map((group) => (group.id === id ? update(group) : group)));

  const moveCard = (groupId: string, index: number, delta: number) => {
    const group = groups.find((entry) => entry.id === groupId);
    const targetIndex = index + delta;
    if (!group || targetIndex < 0 || targetIndex >= group.cardIds.length) return;
    const movedId = group.cardIds[index];
    const targetId = group.cardIds[targetIndex];
    setMovedFrames((current) => {
      const positioned = cards.map((card) => ({ ...card, ...(current[card.id] ? { layout: current[card.id] } : {}) }));
      const source = positioned.find((card) => card.id === movedId)?.layout;
      const target = positioned.find((card) => card.id === targetId)?.layout;
      const swapped = source && target ? swapFrames(positioned, { i: movedId, ...source }, target) : undefined;
      return swapped ? { ...current, ...swapped } : current;
    });
    updateGroup(groupId, (entry) => ({ ...entry, cardIds: moved(entry.cardIds, index, delta) }));
  };

  const moveSection = (id: string, delta: number) =>
    setGroups((current) => {
      const index = current.findIndex((group) => group.id === id);
      return index < 0 ? current : moved(current, index, delta);
    });

  const moveCardToGroup = (cardId: string, destination: string) =>
    setGroups((current) => {
      const without = current.map((group) => ({ ...group, cardIds: group.cardIds.filter((id) => id !== cardId) }));
      return without.map((group) =>
        group.id === destination ? { ...group, cardIds: [...group.cardIds, cardId] } : group,
      );
    });

  const setCardFrame = (cardId: string, patch: Partial<CardFrame>) => {
    setResizedCardIds((current) => new Set(current).add(cardId));
    setFrames((current) => ({ ...current, [cardId]: { ...current[cardId], ...patch } }));
  };

  const addSection = () => {
    const title = newSectionTitle.trim();
    if (!title) return;
    setGroups((current) => [
      ...current,
      {
        id: makeSectionId(
          title,
          current.map((group) => group.id),
        ),
        title,
        cardIds: [],
      },
    ]);
    setNewSectionTitle('');
  };

  const buildDraft = () => {
    const resolved = groups.map((group, index) =>
      group.id === UNGROUPED && (index > 0 || group.title.trim())
        ? {
            ...group,
            id: makeSectionId(
              group.title || 'section',
              groups.map((entry) => entry.id),
            ),
          }
        : group,
    );
    const nextSections = resolved
      .filter((group) => group.id !== UNGROUPED)
      .map((group) => ({ id: group.id, title: group.title.trim() }));
    const nextCards = resolved.flatMap((group) =>
      group.cardIds
        .map((id) => cardsById.get(id))
        .filter((card): card is LensCard => Boolean(card))
        .map((card) => {
          const next = {
            ...card,
            ...frames[card.id],
            ...(arrangedTitleAlignments[card.id]
              ? { config: { ...card.config, titleAlignment: arrangedTitleAlignments[card.id] } }
              : {}),
            ...(movedFrames[card.id] ? { layout: movedFrames[card.id] } : {}),
            section: group.id === UNGROUPED ? undefined : group.id,
          };
          const frame = frameForCard(next);
          return next.layout
            ? {
                ...next,
                layout: resizedCardIds.has(card.id)
                  ? { ...next.layout, w: frame.w, h: frame.h, x: Math.min(next.layout.x, 12 - frame.w) }
                  : { ...next.layout, x: Math.min(next.layout.x, 12 - next.layout.w) },
              }
            : next;
        }),
    );
    return { sections: nextSections, cards: nextCards };
  };

  const save = () => {
    if (structureChanged) return;
    return onSave(buildDraft());
  };

  const autoArrange = () => {
    if (!onAutoArrange || structureChanged) return;
    const arranged = onAutoArrange(buildDraft().cards);
    setMovedFrames(Object.fromEntries(arranged.map((card) => [card.id, card.layout])));
    setArrangedTitleAlignments(
      Object.fromEntries(
        arranged.flatMap((card) => (card.config?.titleAlignment ? [[card.id, card.config.titleAlignment]] : [])),
      ),
    );
    setResizedCardIds(new Set());
    setHasArrangementPreview(true);
  };

  const workspaceTitle = onAutoArrange ? 'Organise cards' : 'Manage layout';

  const removeSection = () => {
    if (!removing) return;
    setGroups((current) => {
      const removed = current.find((group) => group.id === removing.id);
      if (!removed) return current;
      return current
        .map((group) =>
          group.id === removing.destination ? { ...group, cardIds: [...group.cardIds, ...removed.cardIds] } : group,
        )
        .filter((group) => group.id !== removed.id);
    });
    setRemoving(null);
  };

  return (
    <>
      <Dialog fullWidth maxWidth="md" onClose={isSaving ? undefined : onClose} open={open}>
        <DialogTitle sx={{ pb: 0.75 }}>{workspaceTitle}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Stack spacing={2}>
            {structureChanged && (
              <Alert severity="warning">
                This Lens’s layout changed while you were editing. Close and reopen {workspaceTitle} to refresh before
                saving. Your pending edits are still shown here.
              </Alert>
            )}
            <Typography color="text.secondary" variant="body2">
              Organise sections, reorder cards and adjust their size.
            </Typography>
            {onAutoArrange && (
              <Stack alignItems="flex-start" spacing={1}>
                <Button disabled={isSaving || structureChanged} onClick={autoArrange} size="small" variant="outlined">
                  Auto-arrange
                </Button>
                <Typography color="text.secondary" variant="body2">
                  Preview a tidy layout. Changes apply when you save.
                </Typography>
              </Stack>
            )}
            {hasArrangementPreview && <LayoutPreview {...buildDraft()} />}

            <Stack direction={{ sm: 'row', xs: 'column' }} spacing={1} sx={{ alignItems: { sm: 'flex-end' } }}>
              <TextField
                disabled={isSaving}
                fullWidth
                label="New section"
                onChange={(event) => setNewSectionTitle(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') addSection();
                }}
                placeholder="For example, Performance overview"
                size="small"
                value={newSectionTitle}
              />
              <Button
                disabled={isSaving || !newSectionTitle.trim()}
                onClick={addSection}
                startIcon={<AddIcon />}
                variant="outlined"
              >
                Add section
              </Button>
            </Stack>

            {groups.map((group, groupIndex) => {
              const isUngrouped = group.id === UNGROUPED;
              if (isUngrouped && group.cardIds.length === 0 && groups.length > 1) return null;
              return (
                <Paper key={group.id} sx={{ overflow: 'hidden' }} variant="outlined">
                  <Stack
                    alignItems={{ sm: 'center' }}
                    direction={{ sm: 'row', xs: 'column' }}
                    spacing={1}
                    sx={{ bgcolor: 'action.hover', p: 1.25 }}
                  >
                    <TextField
                      disabled={isSaving}
                      fullWidth
                      label={isUngrouped ? 'No-section heading (optional)' : 'Section name'}
                      onChange={(event) => updateGroup(group.id, (entry) => ({ ...entry, title: event.target.value }))}
                      placeholder={isUngrouped ? 'No heading' : undefined}
                      size="small"
                      value={group.title}
                    />
                    <Stack direction="row" spacing={0.25}>
                      <Tooltip title="Move section up">
                        <span>
                          <IconButton
                            aria-label={`Move ${group.title || 'unsectioned cards'} up`}
                            disabled={isSaving || groupIndex === 0}
                            onClick={() => moveSection(group.id, -1)}
                            size="small"
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move section down">
                        <span>
                          <IconButton
                            aria-label={`Move ${group.title || 'unsectioned cards'} down`}
                            disabled={isSaving || groupIndex === groups.length - 1}
                            onClick={() => moveSection(group.id, 1)}
                            size="small"
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {!isUngrouped && (
                        <Tooltip title="Remove section">
                          <span>
                            <IconButton
                              aria-label={`Remove ${group.title || 'unnamed section'}`}
                              disabled={isSaving}
                              onClick={() => {
                                const destination = groups.find((entry) => entry.id !== group.id)?.id ?? UNGROUPED;
                                setRemoving({ id: group.id, destination });
                              }}
                              size="small"
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>

                  {group.cardIds.length === 0 ? (
                    <Typography color="text.secondary" sx={{ display: 'block', p: 2 }} variant="body2">
                      No results in this section yet.
                    </Typography>
                  ) : (
                    <Stack divider={<Divider flexItem />}>
                      {group.cardIds.map((cardId, cardIndex) => {
                        const card = cardsById.get(cardId);
                        if (!card) return null;
                        const frame = frames[cardId] ?? {};
                        const size =
                          resolveLensCardPresentation({ size: frame.size, type: card.type }).size ?? DEFAULT_SIZE;
                        return (
                          <Stack
                            alignItems={{ md: 'center' }}
                            direction={{ md: 'row', xs: 'column' }}
                            key={cardId}
                            spacing={1}
                            sx={{ p: 1.25 }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography noWrap title={card.title} variant="body2">
                                {card.title}
                              </Typography>
                              <Typography color="text.secondary" variant="caption">
                                {card.type}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }} useFlexGap>
                              <FormControl size="small" sx={{ minWidth: 112 }}>
                                <InputLabel id={`${cardId}-section`}>Section</InputLabel>
                                <Select
                                  disabled={isSaving}
                                  id={`${cardId}-section`}
                                  label="Section"
                                  labelId={`${cardId}-section`}
                                  onChange={(event) => moveCardToGroup(cardId, event.target.value)}
                                  value={group.id}
                                >
                                  <MenuItem value={UNGROUPED}>No section</MenuItem>
                                  {namedGroups.map((entry) => (
                                    <MenuItem key={entry.id} value={entry.id}>
                                      {entry.title || 'Untitled section'}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <FormControl size="small" sx={{ minWidth: 104 }}>
                                <InputLabel id={`${cardId}-width`}>Width</InputLabel>
                                <Select
                                  disabled={isSaving}
                                  id={`${cardId}-width`}
                                  label="Width"
                                  labelId={`${cardId}-width`}
                                  onChange={(event) =>
                                    setCardFrame(cardId, { size: event.target.value as LensCardSize })
                                  }
                                  value={size}
                                >
                                  {FRAME_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <FormControl size="small" sx={{ minWidth: 110 }}>
                                <InputLabel id={`${cardId}-height`}>Height</InputLabel>
                                <Select
                                  disabled={isSaving}
                                  id={`${cardId}-height`}
                                  label="Height"
                                  labelId={`${cardId}-height`}
                                  onChange={(event) =>
                                    setCardFrame(cardId, {
                                      height:
                                        event.target.value === 'auto'
                                          ? undefined
                                          : (event.target.value as LensCardHeight),
                                    })
                                  }
                                  value={frame.height ?? 'auto'}
                                >
                                  {HEIGHT_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <Tooltip title="Move result up">
                                <span>
                                  <IconButton
                                    aria-label={`Move ${card.title} up`}
                                    disabled={cardIndex === 0 || isSaving}
                                    onClick={() => moveCard(group.id, cardIndex, -1)}
                                    size="small"
                                  >
                                    <ArrowUpwardIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Move result down">
                                <span>
                                  <IconButton
                                    aria-label={`Move ${card.title} down`}
                                    disabled={cardIndex === group.cardIds.length - 1 || isSaving}
                                    onClick={() => moveCard(group.id, cardIndex, 1)}
                                    size="small"
                                  >
                                    <ArrowDownwardIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </Stack>
                        );
                      })}
                    </Stack>
                  )}
                </Paper>
              );
            })}
            {hasUnnamedSection && <Alert severity="warning">Give each section a name before saving.</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={isSaving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={isSaving || hasUnnamedSection || structureChanged}
            onClick={() => void save()}
            variant="contained"
          >
            {isSaving ? 'Saving…' : 'Save layout'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog fullWidth maxWidth="xs" onClose={() => setRemoving(null)} open={Boolean(removing)}>
        <DialogTitle>Remove this section?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography color="text.secondary" variant="body2">
              Choose whether its cards move to another section or are removed from this Lens.
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="layout-remove-section-destination">Results</InputLabel>
              <Select
                id="layout-remove-section-destination-select"
                label="Results"
                labelId="layout-remove-section-destination"
                onChange={(event) =>
                  setRemoving((current) => (current ? { ...current, destination: event.target.value } : current))
                }
                value={removing?.destination ?? UNGROUPED}
              >
                <MenuItem value={UNGROUPED}>Move to no section</MenuItem>
                {groups
                  .filter((group) => group.id !== removing?.id && group.id !== UNGROUPED)
                  .map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      Move to {group.title || 'Untitled section'}
                    </MenuItem>
                  ))}
                <MenuItem value={DELETE_CARDS}>Remove these results</MenuItem>
              </Select>
            </FormControl>
            {removing?.destination === DELETE_CARDS && (
              <Alert severity="warning">
                Removing the section will also remove its cards and queries from this Lens.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)}>Cancel</Button>
          <Button
            color={removing?.destination === DELETE_CARDS ? 'error' : 'primary'}
            onClick={removeSection}
            variant="contained"
          >
            {removing?.destination === DELETE_CARDS ? 'Remove results' : 'Remove section'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function makeSectionId(title: string, existing: Array<string>): string {
  const base =
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .replace(/^([^a-z])/, 'section-$1')
      .slice(0, 58) || 'section';
  const ids = new Set(existing);
  if (!ids.has(base)) return base;
  let suffix = 2;
  while (ids.has(`${base}-${suffix}`)) suffix += 1;
  return `${base}-${suffix}`;
}
